"use client";

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import { useAccountModal } from "@/contexts/account-modal-context";
import { useAuth } from "@/contexts/auth-context";

/**
 * Inline keyframes, same definitions live in `globals.css` for editorial
 * use, but Turbopack / Tailwind v4 sometimes tree-shake or fail to surface
 * `@keyframes` blocks to the runtime. Injecting them once via a stylesheet
 * appended to `document.head` makes the welcome card animations
 * self-contained and immune to CSS pipeline quirks. Same approach as the
 * design mockup (`injectWelcomeCSS()`).
 */
const WC_KEYFRAMES_ID = "welcome-card-keyframes";
const WC_KEYFRAMES_CSS = `
@keyframes wc-spin { to { transform: rotate(360deg); } }
@keyframes wc-bar-indet {
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(220%); }
}
@keyframes wc-pulse-dot {
  0%, 100% { opacity: 0.35; transform: scale(0.85); }
  50%      { opacity: 1;    transform: scale(1); }
}
@keyframes wc-fade-step {
  0%   { opacity: 0; transform: translateY(4px); }
  12%  { opacity: 1; transform: translateY(0); }
  88%  { opacity: 1; transform: translateY(0); }
  100% { opacity: 0; transform: translateY(-4px); }
}
`;

function useInjectKeyframes() {
  useEffect(() => {
    if (document.getElementById(WC_KEYFRAMES_ID) !== null) {
      return;
    }

    const style = document.createElement("style");
    style.id = WC_KEYFRAMES_ID;
    style.textContent = WC_KEYFRAMES_CSS;
    document.head.appendChild(style);
  }, []);
}

/** "welcome" → standard editorial hero. `null` hides the card entirely. */
type WelcomeBootPhase = "welcome";

type WelcomeStudioCardProps = {
  bootPhase: WelcomeBootPhase | null;
  /**
   * Whether the studio is ready to consume the user's "Entrer" click.
   * When false, the CTA shows an inline spinner, the kicker swaps to a
   * pulsing "INITIALISATION", a hairline progress bar runs at the bottom
   * of the card, and the footer cycles through reassurance steps.
   */
  ready?: boolean;
  onEnter: () => void;
};

const COLORS = {
  border: "rgba(255, 255, 255, 0.06)",
  text: "#e8e8ed",
  textDim: "#7a7a85",
  /* Le pied de carte, « Se connecter » et la mention de bêta. Trois textes de
     10 à 11 px, donc trois fois le seuil normal de WCAG AA (4,5:1).

     Le #6a6a78 précédent ne le tenait pas : 3,64:1 sur le fond composé de la
     carte (~#0d0d10), malgré le commentaire qui affirmait le contraire. Cette
     valeur donne 5,7:1. Une divulgation de statut illisible n'en est pas une,
     et un retour d'inscrit encore moins. */
  textMute: "#8a8a96",
  violet: "#8b5cf6",
  violetLight: "#c4b5fd",
  violetGlow: "rgba(139, 92, 246, 0.32)",
};

const LOADING_STEPS = [
  "Chargement de la bibliothèque sonore",
  "Calibrage du clavier",
  "Préparation du moteur d'analyse",
  "Presque prêt",
];

/**
 * Cycles through `LOADING_STEPS` at a fixed interval while `active` is true,
 * then resets to step 0 when it goes false. The returned string is keyed in
 * the rendering JSX so the `wc-fade-step` animation re-fires on each change.
 */
function useCycledStep(active: boolean, intervalMs = 1500): string {
  const [i, setI] = useState(0);

  useEffect(() => {
    if (!active) {
      setI(0);

      return;
    }

    const id = setInterval(() => {
      setI((x) => (x + 1) % LOADING_STEPS.length);
    }, intervalMs);

    return () => clearInterval(id);
  }, [active, intervalMs]);

  return LOADING_STEPS[i];
}

type WelcomeContentProps = {
  /**
   * Whether the card is actually on screen. The card is never unmounted
   * (it fades out via CSS), so without this the `Enter` shortcut below
   * would stay armed on `window` for the whole session and re-fire the
   * boot sequence — jingle track re-added, transport played, timeline
   * reset — on every `Enter` pressed anywhere in the Studio (the learn
   * journey uses `Enter` as its primary action).
   */
  active: boolean;
  ready: boolean;
  onEnter: () => void;
};

function WelcomeContent({ active, ready, onEnter }: WelcomeContentProps) {
  // Lié à `active` : la carte n'est jamais démontée, et sans cette garde le
  // cycle de 1,5 s continuait toute la session, carte cachée ou hors studio.
  const loading = active && !ready;
  const step = useCycledStep(loading);

  useEffect(() => {
    if (!active) {
      return;
    }

    const handler = (e: KeyboardEvent) => {
      if (e.key === "Enter" && ready) {
        e.preventDefault();
        onEnter();
      }
    };

    window.addEventListener("keydown", handler);

    return () => window.removeEventListener("keydown", handler);
  }, [active, ready, onEnter]);

  return (
    <>
      <div
        className="mb-7 inline-flex items-center [@media(max-height:480px)]:mb-3"
        style={{ gap: 12, height: 12 }}
      >
        <span
          aria-hidden="true"
          className="block"
          style={{ width: 26, height: 1, background: COLORS.violetLight, opacity: 0.55 }}
        />
        {loading ? (
          <div className="inline-flex items-center" style={{ gap: 8 }}>
            <span
              aria-hidden="true"
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: COLORS.violetLight,
                animation: "wc-pulse-dot 1.4s ease-in-out infinite",
                boxShadow: `0 0 8px ${COLORS.violetGlow}`,
              }}
            />
            <span
              style={{
                fontFamily: "var(--font-geist-mono), ui-monospace, monospace",
                fontSize: 10,
                letterSpacing: "0.32em",
                color: COLORS.violetLight,
                fontWeight: 500,
              }}
            >
              INITIALISATION
            </span>
          </div>
        ) : (
          <span
            style={{
              fontFamily: "var(--font-geist-mono), ui-monospace, monospace",
              fontSize: 10,
              letterSpacing: "0.32em",
              color: COLORS.violetLight,
              fontWeight: 500,
            }}
          >
            VIRTUOSA
          </span>
        )}
        <span
          aria-hidden="true"
          className="block"
          style={{ width: 26, height: 1, background: COLORS.violetLight, opacity: 0.55 }}
        />
      </div>

      {/*
        Un `p`, pas un `h1` : la carte est un dialogue plein écran qui porte
        déjà son nom accessible (`aria-label`), et elle reste montée sur toutes
        les routes — son titre entrait donc en concurrence avec le `h1` de la
        page réellement affichée, la fiche d'un morceau par exemple. Le rendu
        visuel est identique.
      */}
      <p
        className="m-0 text-[56px] [@media(max-height:480px)]:text-[30px]"
        style={{
          fontFamily: "var(--font-fraunces), serif",
          fontWeight: 300,
          lineHeight: 1.02,
          letterSpacing: "-0.03em",
          color: COLORS.text,
        }}
      >
        Bienvenue.
      </p>

      <p
        className="mx-auto mt-5 mb-9 text-[22px] [@media(max-height:480px)]:mt-2 [@media(max-height:480px)]:mb-3.5 [@media(max-height:480px)]:text-[15px]"
        style={{
          maxWidth: 460,
          fontFamily: "var(--font-fraunces), serif",
          fontStyle: "italic",
          fontWeight: 300,
          lineHeight: 1.4,
          color: COLORS.violetLight,
          letterSpacing: "-0.01em",
          textWrap: "balance",
        }}
      >
        Une nouvelle ère de l&apos;apprentissage
        <br />
        de la musique au piano.
      </p>

      <button
        type="button"
        onClick={loading ? undefined : onEnter}
        disabled={loading}
        autoFocus
        aria-busy={loading || undefined}
        className="inline-flex items-center justify-center px-7 py-[15px] text-[14px] [@media(max-height:480px)]:px-5 [@media(max-height:480px)]:py-2.5 [@media(max-height:480px)]:text-[13px]"
        style={{
          gap: 12,
          background: loading ? "rgba(139, 92, 246, 0.22)" : COLORS.violet,
          color: loading ? COLORS.violetLight : "#fff",
          border: `1px solid ${loading ? "rgba(139, 92, 246, 0.35)" : COLORS.violet}`,
          borderRadius: 10,
          cursor: loading ? "not-allowed" : "pointer",
          fontFamily: "inherit",
          fontWeight: 500,
          boxShadow: loading
            ? "none"
            : `0 0 26px ${COLORS.violetGlow}, inset 0 1px 0 rgba(255, 255, 255, 0.18)`,
          opacity: loading ? 0.92 : 1,
          transition:
            "background 240ms ease, color 240ms ease, box-shadow 240ms ease, border-color 240ms ease",
          minWidth: 232,
        }}
      >
        {loading ? (
          <>
            <span
              aria-hidden="true"
              style={{
                width: 14,
                height: 14,
                borderRadius: "50%",
                border: "1.5px solid rgba(196, 181, 253, 0.25)",
                borderTopColor: COLORS.violetLight,
                animation: "wc-spin 0.9s linear infinite",
                display: "inline-block",
              }}
            />
            <span>Préparation du studio…</span>
          </>
        ) : (
          <>
            Entrer dans le studio
            <span style={{ fontSize: 16 }} aria-hidden="true">
              →
            </span>
          </>
        )}
      </button>

      <div
        className="mt-[22px] [@media(max-height:480px)]:mt-2.5"
        style={{
          height: 14,
          fontSize: 11,
          color: loading ? COLORS.textDim : COLORS.textMute,
          fontFamily: "var(--font-geist-mono), ui-monospace, monospace",
          letterSpacing: "0.14em",
        }}
      >
        {loading ? (
          <span
            key={step}
            style={{
              display: "inline-block",
              animation: "wc-fade-step 1.5s ease-in-out",
              textTransform: "uppercase",
            }}
          >
            {step}
          </span>
        ) : (
          <span>PRESSE ↵ POUR CONTINUER</span>
        )}
      </div>
    </>
  );
}

/**
 * Editorial card hero rendered above the studio: the "Entrer dans le studio"
 * CTA, with an inline preload narrative driven by `ready`.
 *
 * Visibility is driven by the parent's `bootPhase`. Passing `null` hides the
 * card entirely (used during SSR, before the boot phase is resolved).
 */
export function WelcomeStudioCard({
  bootPhase,
  ready = true,
  onEnter,
}: WelcomeStudioCardProps) {
  useInjectKeyframes();

  const { isAuthenticated } = useAuth();
  const { openAccountModal, isAccountModalOpen } = useAccountModal();
  const dialogRef = useRef<HTMLDivElement | null>(null);

  const visible = bootPhase === "welcome";
  /* L'état est porté par le nom du dialogue, pas seulement par la mention
     visible : celle-ci est un `span` non focusable, et le focus part droit sur
     le CTA, donc un lecteur d'écran en mode formulaire ne l'annoncerait
     jamais. La légère redondance en mode exploration est le bon arbitrage,
     masquer un texte de statut aux lecteurs d'écran serait le mauvais. */
  const ariaLabel = "Bienvenue sur Virtuosa, bêta publique";
  const loading = visible && !ready;

  // Focus trap. The card is full-screen blocking; Tab must cycle within it.
  // Implemented as a wrap-around on first/last focusable rather than a full
  // sentinel-pair pattern, the simpler version is enough here.
  const handleDialogKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLDivElement>) => {
      if (e.key !== "Tab") {
        return;
      }

      const dialog = dialogRef.current;

      if (!dialog) {
        return;
      }

      const focusables = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'input:not([disabled]), button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
        ),
      );

      if (focusables.length === 0) {
        return;
      }

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;

      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();

        return;
      }

      if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [],
  );

  return (
    <div
      aria-hidden={!visible}
      className={`fixed inset-safe z-[80] transition-opacity duration-300 ${
        visible ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
      }`}
    >
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 60% 50% at 50% 50%, rgba(7,7,9,0.78) 0%, rgba(7,7,9,0.93) 60%, rgba(7,7,9,0.97) 100%)",
          backdropFilter: "blur(4px) saturate(80%)",
          WebkitBackdropFilter: "blur(4px) saturate(80%)",
          pointerEvents: "none",
        }}
      />

      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.6) 100%)",
        }}
      />

      <div className="absolute inset-0 flex items-center justify-center px-6 py-6 pointer-events-none [@media(max-height:480px)]:py-3">
        <div
          ref={dialogRef}
          role="dialog"
          aria-label={ariaLabel}
          aria-modal="true"
          onKeyDown={handleDialogKeyDown}
          className={`relative max-h-full w-[720px] max-w-full overflow-x-hidden overflow-y-auto rounded-2xl transition-all duration-300 ease-out ${
            visible
              ? "scale-100 opacity-100 pointer-events-auto"
              : "scale-[0.98] opacity-0 pointer-events-none"
          }`}
          style={{
            background: "rgba(13, 13, 17, 0.93)",
            backdropFilter: "blur(24px) saturate(140%)",
            WebkitBackdropFilter: "blur(24px) saturate(140%)",
            border: `1px solid ${COLORS.border}`,
            boxShadow:
              "0 30px 80px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.04)",
            fontFamily: "var(--font-inter), Inter, sans-serif",
          }}
        >
          {/*
            Sans ce lien, un inscrit qui revient est bloque : la carte
            couvre entierement le header, donc le bouton « Se connecter »
            qui y vit est inatteignable tant qu on n est pas entre dans le
            studio.

            Dans le coin, jamais sous le bouton principal : la composition
            centree est ce qui fait la carte, et l incitation au compte n a
            rien a faire au premier plan d un premier contact.
          */}
          {/*
            L'état du produit au premier contact, en miroir de « Se connecter ».

            Dans le coin, et pas dans la composition : le kicker bascule sur
            « INITIALISATION » pendant le préchargement, une mention greffée
            dessus apparaîtrait puis disparaîtrait dans les deux premières
            secondes ; et toute rangée ajoutée dans la colonne centrale pousse
            la titraille sérif, ce qui casse en téléphone couché où le
            rembourrage est déjà tombé à 20 px.

            Du type nu, pas une pastille : la composition ne contient aucune
            boîte à part le CTA. Un chip arrondi y serait le seul autre
            rectangle et se lirait comme un autocollant. La pastille, c'est
            dans le header, où tout est déjà chip ou bouton.

            Inerte, alors que celle du header s'ouvre : `Popover` passe par
            `createPortal`, donc son panneau se rendrait hors de `dialogRef`,
            que le piège de focus interroge — Tab en retomberait dans la carte.
            Et un focusable de plus avant le CTA `autoFocus` est un arrêt
            gratuit sur le chemin de l'action principale.
          */}
          <span
            className="absolute left-4 top-[13px] z-10 [@media(max-height:480px)]:left-3 [@media(max-height:480px)]:top-2.5"
            style={{
              fontFamily: "var(--font-geist-mono), ui-monospace, monospace",
              fontSize: 10,
              fontWeight: 500,
              letterSpacing: "0.2em",
              lineHeight: "14px",
              color: COLORS.textMute,
            }}
          >
            BÊTA PUBLIQUE
          </span>

          {!isAuthenticated ? (
            <button
              type="button"
              onClick={() => openAccountModal("welcome_card")}
              className="absolute right-4 top-3 z-10 text-[11px] tracking-tight transition-colors duration-150 cursor-pointer"
              style={{ color: COLORS.textMute }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = COLORS.text;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = COLORS.textMute;
              }}
            >
              Se connecter
            </button>
          ) : null}

          <div
            aria-hidden="true"
            className="pointer-events-none absolute"
            style={{
              top: -180,
              left: "50%",
              transform: "translateX(-50%)",
              width: 520,
              height: 360,
              background:
                "radial-gradient(ellipse, rgba(139,92,246,0.22), transparent 65%)",
            }}
          />

          {/*
            Le rembourrage a deux branches, parce que la carte peut manquer
            de place dans les deux sens : un téléphone debout rogne la
            largeur, un téléphone couché rogne la hauteur — et la seconde
            situation, la seule où la titraille sérif ne tient plus, était
            invisible pour un seuil indexé sur la largeur.

            Écrit en CSS et non lu depuis un hook de media query : ces
            derniers annoncent leur valeur mobile au premier rendu, et le
            premier écran de l'app est précisément celui où un battement de
            mise en page se voit.
          */}
          <div className="relative p-[52px_56px_44px] text-center [@media(max-width:419px)]:p-[40px_24px_32px] [@media(max-height:480px)]:p-[20px_28px_18px]">
            {/*
              `active` désarme le raccourci `Enter`, lié sur `window` avec un
              `preventDefault()`. Il faut le désarmer aussi quand l'écran de
              compte est ouvert par-dessus : sinon `Entrée` y annule la
              soumission du formulaire et démarre le Studio à la place — et
              l'écran affiche justement « PRESSE ↵ POUR CONTINUER ».
            */}
            <WelcomeContent
              active={visible && !isAccountModalOpen}
              ready={ready}
              onEnter={onEnter}
            />
          </div>

          {loading && (
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                height: 2,
                background: "rgba(139, 92, 246, 0.08)",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  bottom: 0,
                  left: 0,
                  width: "45%",
                  background: `linear-gradient(90deg, transparent, ${COLORS.violet} 35%, ${COLORS.violetLight} 60%, transparent)`,
                  animation: "wc-bar-indet 1.6s cubic-bezier(0.65, 0, 0.35, 1) infinite",
                  boxShadow: `0 0 12px ${COLORS.violetGlow}`,
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
